/**
 * Import de notes depuis un fichier CSV ou XLSX.
 *
 * La modale convient à une note, le calendrier à une dizaine. Quand les notes existent déjà
 * ailleurs — un tableau d'opérations commerciales, un calendrier éditorial, un journal
 * d'incidents tenu dans Excel — il faut pouvoir les verser d'un coup.
 *
 * **Rien ne part sans avoir été vu.** Le fichier est lu dans le navigateur, chaque ligne
 * validée et montrée avec ses fautes, et seules les lignes cochées sont envoyées. C'est le
 * seul garde-fou possible : une fois écrites, les notes ne se suppriment qu'une à une, et
 * rien en base ne relie celles d'un même import.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Upload, Download, Loader, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { enFrancais } from './PeriodSelector';
import { libelleSujet, sujetDeNote } from '../constants/noteTopics';
import PorteeDeNote, { STYLE_SELECTEUR } from './notes/PorteeDeNote';
import { usePorteeDeNote } from './notes/usePorteeDeNote';
import {
  lireFichier, analyserLignes, modeleCsv, COLONNES, EXTENSIONS, LIGNES_MAX
} from '../utils/lireTableur';

const API_URL = config.API_URL;

/** Clé de rapprochement d'une note existante avec une ligne du fichier. */
const cleDeNote = (contenu, debut) => `${String(contenu || '').trim()}|${debut}`;

const NotesImport = ({ token, clients = [], sites = [], accounts = [], selectedClientId, selectedSiteId }) => {
  const [feuilles, setFeuilles] = useState(null);
  const [feuilleChoisie, setFeuilleChoisie] = useState(0);
  const [nomFichier, setNomFichier] = useState('');
  const [erreur, setErreur] = useState('');
  const [lecture, setLecture] = useState(false);
  const [survol, setSurvol] = useState(false);

  /** Numéros de ligne exclus par l'utilisateur ou par la détection de doublons. */
  const [exclues, setExclues] = useState(() => new Set());
  const [dejaPresentes, setDejaPresentes] = useState(() => new Set());
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);

  const champFichier = useRef(null);

  /**
   * Changer de portée invalide l'aperçu : les marques de doublon ont été calculées pour une
   * autre portée, et les garder afficherait des exclusions qui ne veulent plus rien dire.
   */
  const oublierAnalyse = useCallback(() => {
    setDejaPresentes(new Set());
    setResultat(null);
  }, []);

  const portee = usePorteeDeNote({
    sites,
    accounts,
    selectedClientId,
    selectedSiteId,
    auChangement: oublierAnalyse
  });

  const analyse = useMemo(() => {
    if (!feuilles || !feuilles[feuilleChoisie]) return null;
    const { entetes, lignes } = feuilles[feuilleChoisie];
    return analyserLignes(entetes, lignes);
  }, [feuilles, feuilleChoisie]);

  // Mémorisé : `analyse?.lignes || []` fabriquait un tableau neuf à chaque rendu quand aucun
  // fichier n'est chargé, ce qui recalculait tous les dérivés et recréait la recherche de
  // doublons à chaque fois.
  const lignes = useMemo(() => analyse?.lignes || [], [analyse]);

  const retenues = useMemo(() => lignes.filter(l => (
    l.erreurs.length === 0 && !exclues.has(l.numero)
  )), [lignes, exclues]);

  const compte = useMemo(() => ({
    lues: lignes.length,
    fautives: lignes.filter(l => l.erreurs.length > 0).length,
    doublons: lignes.filter(l => l.doublonInterne || dejaPresentes.has(l.numero)).length,
    retenues: retenues.length
  }), [lignes, dejaPresentes, retenues]);

  // ------------------------------------------------------------- La lecture

  const traiterFichier = useCallback(async (fichier) => {
    if (!fichier) return;
    setErreur('');
    setResultat(null);
    setDejaPresentes(new Set());
    setExclues(new Set());
    setFeuilles(null);
    setNomFichier(fichier.name);
    setLecture(true);

    try {
      const lues = await lireFichier(fichier);
      setFeuilles(lues);
      setFeuilleChoisie(0);
    } catch (e) {
      setErreur(e.message || 'Le fichier n\'a pas pu être lu.');
      setNomFichier('');
    } finally {
      setLecture(false);
    }
  }, []);

  const auDepot = (e) => {
    e.preventDefault();
    setSurvol(false);
    traiterFichier(e.dataTransfer.files?.[0]);
  };

  const telechargerModele = () => {
    const lien = document.createElement('a');
    const url = URL.createObjectURL(new Blob([modeleCsv()], { type: 'text/csv;charset=utf-8;' }));
    lien.href = url;
    lien.download = 'modele-import-notes.csv';
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    URL.revokeObjectURL(url);
  };

  // ------------------------------------------------------------ Les doublons

  /**
   * Marque les lignes qui existent déjà, sur la portée choisie.
   *
   * `GET /api/notes` ne rend pas « les notes de cette portée » mais « les notes visibles dans
   * ce contexte » — celles de la portée **ou** les notes globales. S'en tenir à sa réponse
   * ferait passer pour un doublon une ligne qui n'existe pas chez ce client, et l'exclurait
   * en silence. D'où le filtrage strict sur le triplet client / site / compte, ici.
   *
   * Ni `accountId` ni `platform` ne sont transmis à la requête : sans `siteId`, le serveur
   * ajoute `{ siteId: null }` et tronque le résultat.
   */
  const chercherDoublons = useCallback(async () => {
    const valides = lignes.filter(l => l.erreurs.length === 0);
    if (valides.length === 0) return;

    const dates = valides.map(l => l.debut).sort();
    const params = new URLSearchParams({ startDate: dates[0], endDate: dates[dates.length - 1] });
    if (portee.clientId) params.append('clientId', portee.clientId);
    if (portee.siteId) params.append('siteId', portee.siteId);

    try {
      const reponse = await axios.get(`${API_URL}/api/notes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const memePortee = (n) => (
        (n.clientId || null) === (portee.clientId || null)
        && (n.siteId || null) === (portee.siteId || null)
        && (n.accountId || null) === (portee.accountId || null)
      );

      const existantes = new Set(
        (reponse.data.notes || [])
          .filter(memePortee)
          .map(n => cleDeNote(n.content, (n.startDate || '').split('T')[0]))
      );

      const trouvees = new Set(
        valides.filter(l => existantes.has(cleDeNote(l.contenu, l.debut))).map(l => l.numero)
      );
      setDejaPresentes(trouvees);
      // Décochées par défaut, mais pas définitivement : c'est un avertissement, et rien
      // n'interdit d'avoir deux fois la même note à la même date.
      setExclues(precedentes => new Set([...precedentes, ...trouvees]));
    } catch (e) {
      // Un contrôle de doublons qui échoue ne doit pas empêcher l'import : il informe, il ne
      // décide pas.
      setErreur('Les notes déjà enregistrées n\'ont pas pu être vérifiées ; l\'import reste possible.');
    }
  }, [lignes, portee.clientId, portee.siteId, portee.accountId, token]);

  /**
   * Le contrôle se relance à chaque lecture et à chaque changement de portée.
   *
   * Le laisser à un bouton reviendrait à ne protéger que ceux qui savent déjà qu'il existe.
   * `analyse` est mémorisé sur le fichier et la feuille : l'effet ne se rejoue pas au gré
   * des cases qu'on coche.
   */
  useEffect(() => {
    if (!analyse || analyse.erreurEntetes) return;
    chercherDoublons();
    // `chercherDoublons` se recrée à chaque rendu par la faute de `lignes` ; le mettre en
    // dépendance relancerait la requête en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyse, portee.clientId, portee.siteId, portee.accountId]);

  // ------------------------------------------------------------- L'envoi

  const importer = async () => {
    if (retenues.length === 0) return;
    setEnvoi(true);
    setErreur('');

    try {
      const reponse = await axios.post(`${API_URL}/api/notes/import`, {
        portee: portee.corpsDePortee,
        notes: retenues.map(l => ({
          ligne: l.numero,
          content: l.contenu,
          startDate: l.debut,
          endDate: l.fin,
          // Le sujet du fichier l'emporte sur celui du bandeau, qui n'est qu'un défaut.
          topic: l.sujet || portee.sujet || null,
          showOnChart: l.surGraphique === undefined ? portee.surGraphique : l.surGraphique
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });

      setResultat({ creees: reponse.data.creees });
      setFeuilles(null);
      setNomFichier('');
    } catch (e) {
      const donnees = e.response?.data;
      const fautes = (donnees?.fautes || [])
        .map(f => `ligne ${f.ligne ?? '?'} : ${f.message}`)
        .join(' · ');
      setErreur([donnees?.error || 'L\'import a échoué.', fautes].filter(Boolean).join(' — '));
    } finally {
      setEnvoi(false);
    }
  };

  // ------------------------------------------------------------- Le rendu

  const trop = compte.lues > LIGNES_MAX;

  return (
    <div className="step-content">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>
          <Upload size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Importer des notes
        </h2>
        {lecture && <Loader size={14} className="spin" style={{ color: '#94a3b8' }} />}

        <button
          onClick={telechargerModele}
          style={{ ...STYLE_SELECTEUR, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Download size={14} />
          Télécharger le modèle
        </button>
      </div>

      <PorteeDeNote
        portee={portee}
        clients={clients}
        intro="Les notes importées porteront :"
      />

      {erreur && (
        <div style={{
          padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5', color: '#991b1b', fontSize: '13px', marginBottom: '12px'
        }}>
          {erreur}
        </div>
      )}

      {resultat && (
        <div style={{
          padding: '14px 16px', borderRadius: '8px', backgroundColor: '#ecfdf5',
          border: '1px solid #a7f3d0', color: '#065f46', fontSize: '14px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <CheckCircle size={16} />
          {resultat.creees} note{resultat.creees > 1 ? 's' : ''} importée{resultat.creees > 1 ? 's' : ''}.
          Elles apparaissent dès maintenant dans l'archive et dans le calendrier.
        </div>
      )}

      {/* Le dépôt. Deux chemins vers le même geste : glisser le fichier, ou cliquer pour
          l'ouvrir — le glisser-déposer seul laisse de côté ceux qui ne l'attendent pas. */}
      {!analyse && (
        <div
          onClick={() => champFichier.current?.click()}
          onDragOver={e => { e.preventDefault(); setSurvol(true); }}
          onDragLeave={() => setSurvol(false)}
          onDrop={auDepot}
          style={{
            border: `2px dashed ${survol ? '#7c2d12' : '#d1d5db'}`,
            borderRadius: '12px',
            padding: '48px 24px',
            textAlign: 'center',
            backgroundColor: survol ? '#fff7ed' : '#FAF9F5',
            cursor: 'pointer',
            transition: 'background-color 0.15s, border-color 0.15s'
          }}
        >
          <Upload size={28} style={{ color: '#94a3b8', marginBottom: '10px' }} />
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
            Déposez un fichier, ou cliquez pour le choisir
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {EXTENSIONS.join(' ou ')} — colonnes : {COLONNES.join(', ')}
          </div>
          <input
            ref={champFichier}
            type="file"
            accept={EXTENSIONS.join(',')}
            onChange={e => { traiterFichier(e.target.files?.[0]); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {analyse?.erreurEntetes && (
        <div style={{
          padding: '14px 16px', borderRadius: '8px', backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5', color: '#991b1b', fontSize: '13px', marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, marginBottom: '4px' }}>
            <AlertCircle size={15} />
            {nomFichier}
          </div>
          {analyse.erreurEntetes}
        </div>
      )}

      {analyse && !analyse.erreurEntetes && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
            padding: '12px 16px', backgroundColor: 'white', border: '1px solid #e5e7eb',
            borderRadius: '8px', marginBottom: '12px'
          }}>
            <FileText size={15} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{nomFichier}</span>

            {/* Un classeur peut ranger ses données ailleurs que sur la première feuille.
                Prendre la première en silence importerait la mauvaise, ou rien. */}
            {feuilles && feuilles.length > 1 && (
              <select
                value={feuilleChoisie}
                onChange={e => { setFeuilleChoisie(Number(e.target.value)); setExclues(new Set()); setDejaPresentes(new Set()); }}
                style={STYLE_SELECTEUR}
              >
                {feuilles.map((f, i) => (
                  <option key={f.nom || i} value={i}>{f.nom} ({f.lignes.length} lignes)</option>
                ))}
              </select>
            )}

            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              {compte.lues} ligne{compte.lues > 1 ? 's' : ''} lue{compte.lues > 1 ? 's' : ''}
              {' · '}<strong style={{ color: '#065f46' }}>{compte.retenues} à créer</strong>
              {compte.fautives > 0 && <> · <strong style={{ color: '#991b1b' }}>{compte.fautives} en erreur</strong></>}
              {compte.doublons > 0 && <> · {compte.doublons} en double</>}
              {analyse.videsIgnorees > 0 && <> · {analyse.videsIgnorees} vide{analyse.videsIgnorees > 1 ? 's' : ''} ignorée{analyse.videsIgnorees > 1 ? 's' : ''}</>}
            </span>

            <button
              onClick={chercherDoublons}
              title="Revérifier les notes déjà enregistrées sur cette portée"
              style={{ ...STYLE_SELECTEUR, marginLeft: 'auto' }}
            >
              Revérifier les doublons
            </button>
            <button
              onClick={() => { setFeuilles(null); setNomFichier(''); setErreur(''); }}
              title="Choisir un autre fichier"
              style={{ ...STYLE_SELECTEUR, padding: '8px', display: 'flex' }}
            >
              <X size={15} />
            </button>
          </div>

          {trop && (
            <div style={{
              padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d', color: '#92400e', fontSize: '13px', marginBottom: '12px'
            }}>
              {compte.lues} lignes : l'import est limité à {LIGNES_MAX} à la fois.
              Découpez le fichier avant de recommencer.
            </div>
          )}

          <div style={{
            backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb',
            overflow: 'hidden', marginBottom: '16px'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#FAF9F5' }}>
                  {['', 'Ligne', 'Contenu', 'Début', 'Fin', 'Sujet', 'Graphiques', 'État'].map((titre, i) => (
                    <th key={i} style={{
                      padding: '12px 14px', textAlign: 'left', fontSize: '13px', fontWeight: 600,
                      color: '#6b7280', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap'
                    }}>
                      {titre}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignes.map((l, index) => {
                  const fautive = l.erreurs.length > 0;
                  const double = l.doublonInterne || dejaPresentes.has(l.numero);
                  const cochee = !fautive && !exclues.has(l.numero);
                  const sujetFinal = l.sujet || portee.sujet || null;

                  return (
                    <tr key={l.numero} style={{
                      backgroundColor: fautive ? '#fef2f2' : (index % 2 === 0 ? 'white' : '#FAF9F5'),
                      opacity: !fautive && !cochee ? 0.55 : 1
                    }}>
                      <td style={cellule}>
                        <input
                          type="checkbox"
                          checked={cochee}
                          disabled={fautive}
                          onChange={() => setExclues(actuelles => {
                            const suivantes = new Set(actuelles);
                            if (suivantes.has(l.numero)) suivantes.delete(l.numero);
                            else suivantes.add(l.numero);
                            return suivantes;
                          })}
                        />
                      </td>
                      <td style={{ ...cellule, color: '#94a3b8' }}>{l.numero}</td>
                      <td style={{ ...cellule, maxWidth: '340px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.contenu || <em style={{ color: '#9ca3af' }}>vide</em>}
                        </div>
                      </td>
                      {/* `enFrancais` découpe la chaîne sans construire de `Date` : passer par
                          `toLocaleDateString` afficherait la veille dans un fuseau négatif, et
                          l'aperçu contredirait le calendrier. */}
                      <td style={cellule}>{l.debut ? enFrancais(l.debut) : '—'}</td>
                      <td style={cellule}>{l.fin ? enFrancais(l.fin) : '—'}</td>
                      <td style={cellule}>
                        {sujetFinal ? (
                          <span style={{
                            padding: '1px 7px', borderRadius: '20px', fontSize: '11px', fontWeight: 500,
                            color: sujetDeNote(sujetFinal)?.couleur, backgroundColor: sujetDeNote(sujetFinal)?.fond
                          }}>
                            {libelleSujet(sujetFinal)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={cellule}>
                        {(l.surGraphique === undefined ? portee.surGraphique : l.surGraphique) ? 'oui' : 'non'}
                      </td>
                      <td style={{ ...cellule, fontSize: '12px' }}>
                        {fautive
                          ? <span style={{ color: '#991b1b' }}>{l.erreurs.join(' · ')}</span>
                          : double
                            ? <span style={{ color: '#92400e' }}>
                              {l.doublonInterne ? 'en double dans le fichier' : 'déjà enregistrée'}
                            </span>
                            : <span style={{ color: '#065f46' }}>à créer</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={importer}
              disabled={envoi || trop || retenues.length === 0}
              className="btn btn-primary"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                opacity: (envoi || trop || retenues.length === 0) ? 0.5 : 1
              }}
            >
              {envoi ? <Loader size={15} className="spin" /> : <Upload size={15} />}
              Importer {retenues.length} note{retenues.length > 1 ? 's' : ''}
            </button>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
              Un import ne s'annule pas : les notes se suppriment ensuite une à une.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

const cellule = {
  padding: '12px 14px',
  fontSize: '14px',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'top'
};

export default NotesImport;
